import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import isEmpty from 'lodash.isempty';
import themeColors from '../../assets/styles/themeColors';
import { mediaBelow, pxToRem } from '../../assets/styles/helper';
import Button from '../Button/Button';
import TransactionDetails from '../Modals/Transaction/TransactionDetails';
import { emptyPortfolioTransactionSingle } from '../../store/actions/portfolio/portfolioActions';
import {
  selectPortfolioSingleTransaction,
  selectPortfolioSingleTransactionError,
} from '../../store/selectors/portfolioTransactionsSelector';
import { PORTFOLIO_TRANSACTIONS_SINGLE_LOADING } from '../../store/actions/portfolio/portfolioActionConstants';
import { selectIsLoadingByActionType } from '../../store/selectors/loadingSelectors';
import SectionLoader from '../Loader/SectionLoader';
import { variables } from '../../assets/styles/variables';
import { ReactComponent as GreenCheck } from '../../assets/images/priceAlerts/checked-green.svg';
import { ReactComponent as InfoIcon } from '../../assets/images/priceAlerts/info-blue.svg';
import { ReactComponent as AlertCloseIcon } from '../../assets/images/priceAlerts/alert-close-icon.svg';
import { InfoBanner } from './Common';

const Container = styled.div`
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: ${pxToRem(16)};
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  background-color: #fff;
  position: relative;
`;

const CloseButton = styled.button`
  position: absolute;
  top: ${pxToRem(16)};
  right: ${pxToRem(16)};
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 12.5px;
    height: 12.5px;
  }
`;

const ContentWrapper = styled.div`
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
`;

const MainText = styled.p`
  font-family: ${variables.family.goldSilver};
  font-weight: 500;
  font-size: 18px;
  line-height: 140%;
  text-align: center;
  margin-top: ${pxToRem(6.5)};
  margin-bottom: ${pxToRem(8)};
  color: ${themeColors.colorTextPrimary};
`;

const SubText = styled.p`
  font-family: ${variables.family.goldSilver};
  font-weight: 400;
  font-size: 16px;
  line-height: 140%;
  text-align: center;
  margin-top: 0;
  margin-bottom: ${pxToRem(16)};
  color: ${themeColors.colorTextSecondary};
`;

const MoreText = styled.p`
  font-family: ${variables.family.goldSilver};
  font-weight: 500;
  font-size: 14px;
  line-height: 140%;
  letter-spacing: 0%;
  vertical-align: middle;
  margin: 0;
  color: ${themeColors.colorTextSecondary};

  ${mediaBelow(variables.breakpoints.bpMd)} {
    font-size: 12px;
  }
`;

const ButtonContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  flex-shrink: 0;

  button {
    height: 44px;
    padding: 0 ${pxToRem(16)};
    font-size: 16px;
  }
`;

const AlertConfirmation = ({
  closeModal,
  wizardData,
  onButtonClick,
  setGoBackHandler,
  setWizardTitle,
  stepIndex,
  currentStepIndex,
  isGS,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { storageProduct, alertPrice, isSmsChecked } = wizardData;

  const notificationMethods = [
    isGS && t('gtt.labels.email'),
    isSmsChecked && t('gtt.labels.sms'),
  ]
    .filter(Boolean)
    .join(' + ');

  useEffect(() => {
    if (stepIndex === currentStepIndex) {
      setGoBackHandler(null);
      setWizardTitle('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepIndex, currentStepIndex]);

  const [isTransactionDetailsOpen, setIsTransactionDetailsOpen] = useState(
    false,
  );
  const singleTransaction = useSelector(selectPortfolioSingleTransaction);
  const singleTransactionError = useSelector(
    selectPortfolioSingleTransactionError,
  );
  const singleTransactionLoading = useSelector(
    selectIsLoadingByActionType(PORTFOLIO_TRANSACTIONS_SINGLE_LOADING),
  );

  return (
    <>
      <SectionLoader isLoading={singleTransactionLoading}>
        <Container>
          <CloseButton
            onClick={closeModal}
            data-cy="button-alert-confirmation-close-icon"
          >
            <AlertCloseIcon />
          </CloseButton>
          <ContentWrapper>
            <GreenCheck style={{ height: 52, width: 52 }} />
            <MainText>{t('gtt.confirmation.title')}</MainText>
            <SubText>
              {t('gtt.confirmation.text', {
                producName: storageProduct?.ShortCaption,
                interpolation: { escapeValue: false },
              })}
            </SubText>
            <InfoBanner>
              <InfoIcon
                fill={themeColors.colorTextSecondary}
                style={{ width: 15, height: 15 }}
              />
              <MoreText>
                {t('gtt.confirmation.subtext', {
                  alertPrice,
                  notificationMethods,
                })}
              </MoreText>
            </InfoBanner>
          </ContentWrapper>
          <ButtonContainer>
            <Button
              variant="primaryOutlined"
              textTransform="none"
              style={{ width: '100%' }}
              onClick={closeModal}
              data-cy="button-alert-confirmation-close"
            >
              {t('common.close')}
            </Button>
          </ButtonContainer>
        </Container>
      </SectionLoader>

      <TransactionDetails
        isOpen={!isEmpty(singleTransaction) && isTransactionDetailsOpen}
        onClose={() => {
          dispatch(emptyPortfolioTransactionSingle());
          setIsTransactionDetailsOpen(false);
          onButtonClick();
        }}
        transaction={singleTransaction}
        error={singleTransactionError}
      />
    </>
  );
};

AlertConfirmation.propTypes = {
  closeModal: PropTypes.func,
  wizardData: PropTypes.shape({
    storageProduct: PropTypes.shape({
      ProductCaption: PropTypes.string,
      ProductPrice: PropTypes.number,
      Locations: PropTypes.arrayOf(PropTypes.shape({})),
      ProductCode: PropTypes.string,
      ShortCaption: PropTypes.string,
    }),
    alertPrice: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    isSmsChecked: PropTypes.bool,
    calculation: PropTypes.shape({
      paymentTypeDiscount: PropTypes.number,
      estimatedOrderTotal: PropTypes.number,
      quantity: PropTypes.number,
    }),
    selectedPaymentMethod: PropTypes.shape({
      PaymentMethodType: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
      ]),
    }),
    transactionUid: PropTypes.string,
    selectedAddress: PropTypes.shape({
      City: PropTypes.string,
      StateRegion: PropTypes.string,
      TagType: PropTypes.string,
      TagNote: PropTypes.string,
    }),
    isVaultActiveTab: PropTypes.bool,
    selectedVault: PropTypes.shape({
      LocationType: PropTypes.number,
    }),
    selectedPlan: PropTypes.string,
  }),
  onButtonClick: PropTypes.func,
  setGoBackHandler: PropTypes.func,
  setWizardTitle: PropTypes.func,
  stepIndex: PropTypes.number,
  currentStepIndex: PropTypes.number,
  isGS: PropTypes.bool,
};

export default AlertConfirmation;
